import React from "react";
import { Box } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { formatAxisValue } from "./formatAxisValue";
import { getChartColor } from "visual-components/analytics/campaign/chartColors";

/**
 * Column Grouped Chart Component using MUI X Charts
 * Displays grouped bar chart with multiple series
 * @param {Object} props
 * @param {Object} props.data - Chart data with categories, items, hideMap
 * @param {boolean} props.started - Whether the chart should display data
 * @param {Function} [props.toggleItem] - Function to toggle item visibility
 * @returns {import("react").ReactElement}
 */
export default function ColumnGrouped(props) {
  const { data, started, toggleItem } = props;

  if (!started || !data || !data.categories || !data.items) {
    return (
      <Box sx={{ position: "relative" }}>
        <Box sx={{ width: "100%", minHeight: 300 }} />
      </Box>
    );
  }

  const { categories, items, hideMap = {} } = data;

  if (items.length === 0 || categories.length === 0) {
    return (
      <Box sx={{ position: "relative" }}>
        <Box sx={{ width: "100%", minHeight: 300 }} />
      </Box>
    );
  }

  // Handle legend item click to toggle series visibility
  const handleLegendClick = (_event, context) => {
    if (toggleItem && context.seriesId) {
      toggleItem(context.seriesId);
    }
  };

  // Create series for all items (show all in legend, hidden ones have null data)
  /** @type {import("@mui/x-charts").BarSeries[]} */
  const series = items.map((item, index) => {
    const isHidden = hideMap[item.name];
    const baseColor = getChartColor(index);

    // Apply 40% opacity to color when hidden (for legend visual feedback)
    const displayColor = isHidden ? `${baseColor}66` : baseColor;

    return {
      id: item.name,
      data: isHidden
        ? categories.map(() => null)
        : categories.map((_, idx) => {
            const value =
              item.formattedData && item.formattedData.length
                ? item.formattedData[idx]
                : item.data[idx];
            return value || 0;
          }),
      label: item.title || item.name,
      color: displayColor,
      valueFormatter: (value) => (value !== null ? value?.toLocaleString() : "Hidden"),
    };
  });

  return (
    <BarChart
      xAxis={[
        {
          scaleType: "band",
          data: categories,
          tickLabelStyle: {
            fontSize: 10,
          },
        },
      ]}
      yAxis={[
        {
          disableLine: true,
          valueFormatter: formatAxisValue,
        },
      ]}
      series={series}
      margin={{ bottom: 30 }}
      height={300}
      grid={{ horizontal: true }}
      slotProps={{
        legend: {
          position: {
            vertical: "bottom",
            horizontal: "start",
          },
          sx: {
            fontSize: 12,
          },
          onItemClick: handleLegendClick,
        },
      }}
    />
  );
}
